import { useState, useMemo, useEffect, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { Plus, Pencil, Search, X, Loader2, Sparkles, ChevronLeft, ChevronRight,
  Package, User, Store, Wrench, CreditCard, Calendar, Receipt } from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { buscarCep, codigoIbgeUf } from '@/lib/viacep'
import { enterAbrePrimeiro } from '@/lib/keyboard'
import FilialLiberacoes from '@/components/FilialLiberacoes'
import { TelefoneInput } from '@/components/TelefoneInput'
import { validarDocumento, soDigitos } from '@/lib/masks'
import UltimosCadastros from '@/components/UltimosCadastros'

interface Pessoa {
  id?: number
  codigo?: string
  tipo: string
  pessoa_tipo: string
  nome: string
  nome_fantasia?: string
  // Cliente genérico de balcão (21/10) — só-leitura, muda pelo endpoint dedicado.
  cliente_generico?: boolean
  cliente_generico_motivo?: string
  cliente_generico_em?: string
  documento?: string
  ie?: string
  email?: string
  telefone?: string
  cep?: string
  endereco?: string
  numero?: string
  complemento?: string
  referencia?: string
  bairro?: string
  cidade?: string
  uf?: string
  codigo_ibge_cidade?: string
  codigo_ibge_uf?: string
  codigo_pais?: string
  nome_pais?: string
  indicador_ie?: string
  suframa?: string
  observacao?: string
  ativo: boolean
  pix_chave?: string
  pix_tipo?: string
  banco_codigo?: string
  banco_agencia?: string
  banco_conta?: string
  banco_conta_dv?: string
}

const INDICADOR_IE_OPCOES = [
  { value: '', label: '— selecionar —' },
  { value: '1', label: '1 · Contribuinte ICMS (tem IE)' },
  { value: '2', label: '2 · Isento de IE' },
  { value: '9', label: '9 · Não contribuinte (consumidor final)' },
]

export default function PessoasPage({ tipo }: { tipo: 'cliente' | 'fornecedor' }) {
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [buscandoCep, setBuscandoCep] = useState(false)
  const EMPTY: Pessoa = {
    tipo, pessoa_tipo: 'PF', nome: '', nome_fantasia: '', ativo: true,
    codigo_pais: '1058', nome_pais: 'Brasil',
    indicador_ie: '9',  // default: não contribuinte (mais comum em PF)
  }
  const [editing, setEditing] = useState<Pessoa>(EMPTY)
  // Marcação de cliente genérico de balcão (21/10) — exige senha de admin/gerente.
  const [generico, setGenerico] = useState<Pessoa | null>(null)
  // Trava de CPF/CNPJ já cadastrado: checa no backend (normalizado) enquanto digita.
  const [docDup, setDocDup] = useState<{ codigo?: string; nome: string; tipo_doc: string } | null>(null)
  useEffect(() => {
    const digits = (editing.documento || '').replace(/\D/g, '')
    // Só checa CPF (11) ou CNPJ (14) completos — evita falso alarme no meio da digitação.
    if (digits.length !== 11 && digits.length !== 14) { setDocDup(null); return }
    let vivo = true
    const t = setTimeout(async () => {
      try {
        const r = await api.get('/cadastros/pessoas/checar-documento', {
          params: { documento: digits, excluir_id: editing.id || undefined },
        })
        if (vivo) setDocDup(r.data?.existe ? r.data : null)
      } catch { if (vivo) setDocDup(null) }
    }, 400)
    return () => { vivo = false; clearTimeout(t) }
  }, [editing.documento, editing.id])
  // Validação de CPF/CNPJ (01/07 — pedido do usuário): só permite salvar com dígitos verificadores
  // corretos. Documento vazio é permitido; o erro em vermelho só aparece após completar a digitação
  // (11 dígitos p/ CPF, 14 p/ CNPJ) pra não alarmar no meio da digitação.
  const docDigitsPessoa = soDigitos(editing.documento || '')
  const docInvalido = docDigitsPessoa.length > 0 && !validarDocumento(editing.documento || '', editing.pessoa_tipo)
  // FIX 06/07: mostra "inválido" ao ATINGIR OU EXCEDER o limite (>=), não só no tamanho
  // exato — passar de 11 (CPF) / 14 (CNPJ) dígitos também é inválido e deve alertar.
  const docMostraErro = docInvalido &&
    (editing.pessoa_tipo === 'PF' ? docDigitsPessoa.length >= 11 : docDigitsPessoa.length >= 14)
  const [abaFicha, setAbaFicha] = useState<'cadastro' | 'historico'>('cadastro')
  // sempre abre na aba Cadastro
  useEffect(() => { if (showForm) setAbaFicha('cadastro') }, [showForm, editing.id])

  const PAGE_SIZE = 50
  const [page, setPage] = useState(0)
  // volta pra 1ª página quando muda a busca ou o tipo (cliente/fornecedor)
  useEffect(() => { setPage(0) }, [q, tipo])

  const { data: pageData, isLoading, isFetching } = useQuery<{ rows: Pessoa[]; total: number }>({
    queryKey: ['pessoas', tipo, q, page],
    placeholderData: keepPreviousData,  // mantém a página atual visível enquanto carrega a próxima
    queryFn: async () => {
      const res = await api.get('/cadastros/pessoas', {
        params: { tipo, q: q || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
      })
      return { rows: res.data as Pessoa[], total: Number(res.headers['x-total-count'] ?? res.data.length) }
    },
  })
  const pessoas = pageData?.rows ?? []
  const total = pageData?.total ?? 0
  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const inicio = total === 0 ? 0 : page * PAGE_SIZE + 1
  const fim = Math.min(page * PAGE_SIZE + pessoas.length, total)

  // Saldo de crédito (haver) por cliente — mostrado direto na lista
  const { data: creditosList = [] } = useQuery<{ pessoa_id: number; saldo: number }[]>({
    queryKey: ['pessoas-clientes-credito'],
    queryFn: async () => (await api.get('/financeiro/creditos')).data,
    enabled: tipo === 'cliente',
  })
  const creditoMap = useMemo(() => {
    const m: Record<number, number> = {}
    for (const c of creditosList) m[c.pessoa_id] = c.saldo
    return m
  }, [creditosList])
  const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

  const { data: tabelasPreco = [] } = useQuery<any[]>({
    queryKey: ['tabelas-preco-sel'],
    queryFn: async () => (await api.get('/comercial/tabelas-preco')).data,
    enabled: tipo === 'cliente',
  })
  const { data: rotasFrete = [] } = useQuery<any[]>({
    queryKey: ['rotas-frete-sel'],
    queryFn: async () => (await api.get('/expedicao/rotas')).data,
    enabled: tipo === 'cliente',
  })
  const { data: vendedores = [] } = useQuery<any[]>({
    queryKey: ['vendedores-sel'],
    queryFn: async () => (await api.get('/comercial/vendedores')).data,
    enabled: tipo === 'cliente',
  })

  const save = useMutation({
    mutationFn: async (p: Pessoa) => {
      if (p.id) return (await api.put(`/cadastros/pessoas/${p.id}`, p)).data
      const r = (await api.post('/cadastros/pessoas', p)).data
      return { ...r, _eh_novo: true }
    },
    onSuccess: (res: any) => {
      if (res?._eh_novo && tipo === 'cliente') {
        toast.success(
          'Cliente cadastrado — pendente de liberação em Análise de Crédito (vende só à vista até liberar).',
          { duration: 7000 }
        )
      } else {
        toast.success(`${tipo === 'cliente' ? 'Cliente' : 'Fornecedor'} salvo`)
      }
      qc.invalidateQueries({ queryKey: ['pessoas'] })
      qc.invalidateQueries({ queryKey: ['pessoas-clientes-credito'] })
      setShowForm(false)
      setEditing(EMPTY)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  async function autoPreencherCep() {
    const cep = editing.cep
    if (!cep) return
    setBuscandoCep(true)
    const data = await buscarCep(cep)
    setBuscandoCep(false)
    if (!data) {
      toast.error('CEP não encontrado')
      return
    }
    setEditing(p => ({
      ...p,
      endereco: data.logradouro || p.endereco,
      complemento: data.complemento || p.complemento,
      bairro: data.bairro || p.bairro,
      cidade: data.localidade || p.cidade,
      uf: data.uf || p.uf,
      codigo_ibge_cidade: data.ibge || p.codigo_ibge_cidade,
      codigo_ibge_uf: codigoIbgeUf(data.ibge) || p.codigo_ibge_uf,
    }))
    toast.success('Endereço preenchido pelo CEP ✓')
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900">
            {tipo === 'cliente' ? 'Clientes' : 'Fornecedores'}
          </h1>
          <p className="text-sm text-ink-500">
            Cadastro com dados fiscais completos para emissão de NF-e.
          </p>
        </div>
        <button onClick={() => { setEditing(EMPTY); setShowForm(true) }} className="btn-primary">
          <Plus size={14} /> Novo {tipo === 'cliente' ? 'cliente' : 'fornecedor'}
        </button>
      </div>

      <div className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input className="input pl-8" placeholder="Buscar por nome, CPF/CNPJ ou código..."
            value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={enterAbrePrimeiro(pessoas, p => { setEditing(p); setShowForm(true) })} />
        </div>
      </div>

      <UltimosCadastros
        endpoint="/cadastros/pessoas/recentes"
        params={{ tipo }}
        queryKey={['pessoas-recentes', tipo]}
        rotulo={tipo === 'cliente' ? 'cliente' : 'fornecedor'}
        onSelecionar={async (id) => {
          try {
            const p = (await api.get(`/cadastros/pessoas/${id}`)).data
            setEditing(p); setShowForm(true)
          } catch { toast.error('Não foi possível abrir o cadastro') }
        }}
      />

      <div className="card overflow-hidden">
        <table className="w-full">
          <thead className="bg-ink-50 border-b border-ink-200">
            <tr>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Código</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Nome</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Documento</th>
              <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Cidade/UF</th>
              <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">IE</th>
              {tipo === 'cliente' && <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Crédito</th>}
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && <tr><td colSpan={tipo === 'cliente' ? 7 : 6} className="text-center py-6 text-ink-400">Carregando...</td></tr>}
            {!isLoading && pessoas.length === 0 && (
              <tr><td colSpan={tipo === 'cliente' ? 7 : 6} className="text-center py-6 text-ink-400">Nenhum cadastro</td></tr>
            )}
            {pessoas.map(p => (
              <tr key={p.id}
                onClick={() => { setEditing(p); setShowForm(true) }}
                className="hover:bg-violet-50 cursor-pointer">
                <td className="px-3 py-2.5 font-mono text-xs text-ink-500">{p.codigo || '—'}</td>
                <td className="px-3 py-2.5 text-sm font-medium">{p.nome}</td>
                <td className="px-3 py-2.5 text-xs text-ink-600">
                  {p.cliente_generico
                    ? <span className="text-[10px] font-semibold text-ink-600 bg-ink-100 border border-ink-200 px-1.5 py-0.5 rounded uppercase">genérico</span>
                    : (p.documento || '—')}
                </td>
                <td className="px-3 py-2.5 text-xs text-ink-600">
                  {p.cidade && p.uf ? `${p.cidade}/${p.uf}` : '—'}
                </td>
                <td className="px-3 py-2.5 text-center">
                  {p.indicador_ie === '1' && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">CONTRIB.</span>}
                  {p.indicador_ie === '2' && <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">ISENTO</span>}
                  {p.indicador_ie === '9' && <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">CONSUM.</span>}
                  {!p.indicador_ie && <span className="text-[10px] text-red-500">⚠ sem IE</span>}
                </td>
                {tipo === 'cliente' && (
                  <td className="px-3 py-2.5 text-right text-xs font-semibold whitespace-nowrap">
                    {creditoMap[p.id!] > 0
                      ? <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">{fmtBRL(creditoMap[p.id!])}</span>
                      : <span className="text-ink-300">—</span>}
                  </td>
                )}
                <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setEditing(p); setShowForm(true) }}
                      className="text-ink-500 hover:text-brand-600" title="Editar">
                      <Pencil size={16} />
                    </button>
                    {tipo === 'cliente' && (
                      <button onClick={() => setGenerico(p)}
                        className={p.cliente_generico ? 'text-amber-600' : 'text-ink-300 hover:text-ink-600'}
                        title={p.cliente_generico
                          ? 'Cliente genérico — fecha venda sem cadastro completo'
                          : 'Marcar como cliente genérico (balcão)'}>
                        <Store size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PAGINAÇÃO */}
      {total > 0 && (
        <div className="flex items-center justify-between text-sm text-ink-600">
          <span>
            Mostrando <b>{inicio}</b>–<b>{fim}</b> de <b>{total.toLocaleString('pt-BR')}</b>
            {isFetching && <Loader2 size={13} className="inline ml-2 animate-spin text-ink-400" />}
          </span>
          <div className="flex items-center gap-2">
            <button className="btn-ghost text-sm disabled:opacity-40"
              onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
              <ChevronLeft size={16} /> Anterior
            </button>
            <span className="px-2">Página {page + 1} de {totalPaginas}</span>
            <button className="btn-ghost text-sm disabled:opacity-40"
              onClick={() => setPage(p => Math.min(totalPaginas - 1, p + 1))} disabled={page >= totalPaginas - 1}>
              Próxima <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <h3 className="font-semibold">
                  {editing.id ? `Editar ${editing.codigo ? `#${editing.codigo}` : ''}`
                    : `Novo ${tipo === 'cliente' ? 'Cliente' : 'Fornecedor'}`}
                </h3>
                <button type="button"
                  onClick={() => setEditing(p => ({ ...p, ativo: !p.ativo }))}
                  title={editing.ativo
                    ? `${tipo === 'cliente' ? 'Cliente' : 'Fornecedor'} ativo — clique para inativar`
                    : `${tipo === 'cliente' ? 'Cliente' : 'Fornecedor'} inativo — clique para reativar`}
                  className={`text-xs font-medium px-2 py-0.5 rounded-full border transition ${editing.ativo
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                    : 'bg-rose-50 border-rose-300 text-rose-700 hover:bg-rose-100'}`}>
                  {editing.ativo ? '● Ativo' : '○ Inativo'}
                </button>
              </div>
              <button onClick={() => setShowForm(false)}>
                <X size={18} />
              </button>
            </div>

            {/* ABAS: Cadastro | Histórico (só p/ cliente já salvo) */}
            {editing.id && tipo === 'cliente' && (
              <div className="flex gap-1 px-5 pt-3 border-b sticky top-[57px] bg-white z-10">
                {([['cadastro', 'Cadastro'], ['historico', 'Histórico']] as const).map(([k, label]) => (
                  <button key={k} type="button" onClick={() => setAbaFicha(k)}
                    className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition ${abaFicha === k
                      ? 'border-brand-600 text-brand-700'
                      : 'border-transparent text-ink-500 hover:text-ink-700'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}

            {abaFicha === 'historico' && editing.id ? (
              <ClienteHistorico pessoaId={editing.id} />
            ) : (
            <>
            <div className="p-5 space-y-4">
              {/* SEÇÃO: Identificação */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-ink-500 uppercase tracking-wide">Identificação</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Tipo</label>
                    <select className="input" value={editing.pessoa_tipo}
                      onChange={e => setEditing(p => ({ ...p, pessoa_tipo: e.target.value }))}>
                      <option value="PF">Pessoa Física</option>
                      <option value="PJ">Pessoa Jurídica</option>
                    </select>
                  </div>
                  <div>
                    <label className="label">{editing.pessoa_tipo === 'PF' ? 'CPF' : 'CNPJ'}</label>
                    <input className={`input ${(docDup || docMostraErro) ? 'border-rose-500 focus:ring-rose-500' : ''}`} value={editing.documento || ''}
                      onChange={e => setEditing(p => ({ ...p, documento: e.target.value }))} />
                    {docDup && (
                      <p className="mt-1 text-xs text-rose-600 font-medium">
                        {docDup.tipo_doc} já cadastrado: #{docDup.codigo || ''} {docDup.nome}
                      </p>
                    )}
                    {docMostraErro && !docDup && (
                      <p className="mt-1 text-xs text-rose-600 font-medium">
                        {editing.pessoa_tipo === 'PF' ? 'CPF inválido — verifique os dígitos' : 'CNPJ inválido — verifique os dígitos'}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="label">Nome / Razão Social</label>
                  <input className="input" value={editing.nome}
                    onChange={e => setEditing(p => ({ ...p, nome: e.target.value }))} />
                </div>
                <div>
                  <label className="label">Nome fantasia</label>
                  <input className="input" value={editing.nome_fantasia || ''}
                    onChange={e => setEditing(p => ({ ...p, nome_fantasia: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Telefone</label>
                    <TelefoneInput className="input" value={editing.telefone || ''}
                      onChange={v => setEditing(p => ({ ...p, telefone: v }))} />
                  </div>
                  <div>
                    <label className="label">E-mail</label>
                    <input className="input" value={editing.email || ''}
                      onChange={e => setEditing(p => ({ ...p, email: e.target.value }))} />
                  </div>
                </div>
              </div>

              {/* SEÇÃO: Endereço (com auto-preenchimento CEP) */}
              <div className="space-y-3 pt-2 border-t">
                <h4 className="text-xs font-bold text-ink-500 uppercase tracking-wide">Endereço</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="label">CEP</label>
                    <div className="flex gap-1">
                      <input className="input" maxLength={9} value={editing.cep || ''}
                        onChange={e => setEditing(p => ({ ...p, cep: e.target.value }))}
                        onBlur={autoPreencherCep} placeholder="00000-000" />
                      <button type="button" onClick={autoPreencherCep}
                        disabled={buscandoCep || !editing.cep}
                        className="px-2 bg-brand-100 hover:bg-brand-200 text-brand-700 rounded text-xs disabled:opacity-50"
                        title="Buscar endereço pelo CEP">
                        {buscandoCep ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                      </button>
                    </div>
                  </div>
                  <div className="col-span-2">
                    <label className="label">Logradouro</label>
                    <input className="input" value={editing.endereco || ''}
                      onChange={e => setEditing(p => ({ ...p, endereco: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="label">Número</label>
                    <input className="input" value={editing.numero || ''}
                      onChange={e => setEditing(p => ({ ...p, numero: e.target.value }))} />
                  </div>
                  <div className="col-span-2">
                    <label className="label">Complemento</label>
                    <input className="input" value={editing.complemento || ''}
                      onChange={e => setEditing(p => ({ ...p, complemento: e.target.value }))}
                      placeholder="Apto, sala, bloco..." />
                  </div>
                  <div>
                    <label className="label">Bairro</label>
                    <input className="input" value={editing.bairro || ''}
                      onChange={e => setEditing(p => ({ ...p, bairro: e.target.value }))} />
                  </div>
                  <div className="col-span-3">
                    <label className="label">Ponto de referência</label>
                    <input className="input" value={editing.referencia || ''}
                      onChange={e => setEditing(p => ({ ...p, referencia: e.target.value }))}
                      placeholder="Ex: perto da praça, ao lado do mercado..." />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div className="col-span-2">
                    <label className="label">Cidade</label>
                    <input className="input" value={editing.cidade || ''}
                      onChange={e => setEditing(p => ({ ...p, cidade: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">UF</label>
                    <input className="input" maxLength={2} value={editing.uf || ''}
                      onChange={e => setEditing(p => ({ ...p, uf: e.target.value.toUpperCase() }))} />
                  </div>
                  <div>
                    <label className="label">Cód. IBGE</label>
                    <input className="input font-mono text-xs" maxLength={7}
                      value={editing.codigo_ibge_cidade || ''}
                      onChange={e => setEditing(p => ({ ...p, codigo_ibge_cidade: e.target.value }))}
                      placeholder="2307650" />
                  </div>
                </div>
              </div>

              {/* SEÇÃO: Fiscal (crítico NF-e) */}
              <div className="space-y-3 pt-2 border-t">
                <h4 className="text-xs font-bold text-ink-500 uppercase tracking-wide">
                  Fiscal / NF-e <span className="text-red-500">*</span>
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="label">Indicador de IE</label>
                    <select className="input" value={editing.indicador_ie || ''}
                      onChange={e => setEditing(p => ({ ...p, indicador_ie: e.target.value }))}>
                      {INDICADOR_IE_OPCOES.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="label">Inscrição Estadual</label>
                    <input className="input" value={editing.ie || ''}
                      onChange={e => setEditing(p => ({ ...p, ie: e.target.value }))}
                      disabled={editing.indicador_ie !== '1'}
                      placeholder={editing.indicador_ie === '1' ? '' : 'não aplicável'} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="label">SUFRAMA</label>
                    <input className="input" value={editing.suframa || ''}
                      onChange={e => setEditing(p => ({ ...p, suframa: e.target.value }))}
                      placeholder="só ZFM" />
                  </div>
                  <div>
                    <label className="label">Cód. País</label>
                    <input className="input" value={editing.codigo_pais || '1058'}
                      onChange={e => setEditing(p => ({ ...p, codigo_pais: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label">Nome País</label>
                    <input className="input" value={editing.nome_pais || 'Brasil'}
                      onChange={e => setEditing(p => ({ ...p, nome_pais: e.target.value }))} />
                  </div>
                </div>
              </div>

              {/* SEÇÃO: PIX (fornecedor, pra borderô) / Comercial (cliente) */}
              <div className="pt-3 border-t">
                {String(tipo) !== 'cliente' && (
                  <>
                    <h3 className="text-sm font-semibold text-ink-900 mb-2 flex items-center gap-1.5">
                      💸 Chave PIX
                    </h3>
                    <p className="text-[11px] text-ink-500 mb-2">
                      Pra pagamento via borderô. Quando estiver preenchido, o sistema gera PIX Copia e Cola direto pra esse fornecedor.
                    </p>
                  </>
                )}
                {tipo === 'cliente' && (
                  <h3 className="text-sm font-semibold text-ink-900 mb-2 flex items-center gap-1.5">
                    🧾 Comercial
                  </h3>
                )}
                <div className="grid grid-cols-3 gap-3">
                  {String(tipo) !== 'cliente' && (
                    <div className="col-span-1">
                      <label className="label">Tipo</label>
                      <select className="input" value={editing.pix_tipo || ''}
                        onChange={e => setEditing(p => ({ ...p, pix_tipo: e.target.value }))}>
                        <option value="">— selecionar —</option>
                        <option value="cpf">CPF</option>
                        <option value="cnpj">CNPJ</option>
                        <option value="email">E-mail</option>
                        <option value="celular">Celular</option>
                        <option value="aleatoria">Aleatória (UUID)</option>
                      </select>
                    </div>
                  )}
                  {String(tipo) !== 'cliente' && (
                    <div className="col-span-2">
                      <label className="label">Chave PIX</label>
                      <input className="input" value={editing.pix_chave || ''}
                        placeholder={
                          editing.pix_tipo === 'cpf' ? '000.000.000-00'
                          : editing.pix_tipo === 'cnpj' ? '00.000.000/0000-00'
                          : editing.pix_tipo === 'email' ? 'fornecedor@empresa.com'
                          : editing.pix_tipo === 'celular' ? '+5511999999999'
                          : 'Cole a chave aqui'
                        }
                        onChange={e => setEditing(p => ({ ...p, pix_chave: e.target.value }))} />
                    </div>
                  )}
                  {String(tipo) !== 'cliente' && (
                    <div className="col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3 border-t pt-3 mt-1">
                      <div className="col-span-2 md:col-span-4 text-xs font-semibold text-ink-500">🏦 Dados bancários (pagamento por TED)</div>
                      <div><label className="label">Banco (FEBRABAN)</label><input className="input" placeholder="033" value={editing.banco_codigo || ''} onChange={e => setEditing(p => ({ ...p, banco_codigo: e.target.value }))} /></div>
                      <div><label className="label">Agência</label><input className="input" value={editing.banco_agencia || ''} onChange={e => setEditing(p => ({ ...p, banco_agencia: e.target.value }))} /></div>
                      <div><label className="label">Conta</label><input className="input" value={editing.banco_conta || ''} onChange={e => setEditing(p => ({ ...p, banco_conta: e.target.value }))} /></div>
                      <div><label className="label">Dígito</label><input className="input" maxLength={2} value={editing.banco_conta_dv || ''} onChange={e => setEditing(p => ({ ...p, banco_conta_dv: e.target.value }))} /></div>
                    </div>
                  )}
                  {tipo === 'cliente' && (
                    <div className="col-span-2">
                      <label className="label">Tabela de Preço</label>
                      <select className="input" value={(editing as any).tabela_preco_id || ''}
                        onChange={e => setEditing(p => ({ ...p, tabela_preco_id: e.target.value ? Number(e.target.value) : null } as any))}>
                        <option value="">Preço padrão do produto</option>
                        {tabelasPreco.map((t: any) => <option key={t.id} value={t.id}>{t.nome}</option>)}
                      </select>
                    </div>
                  )}
                  {tipo === 'cliente' && (
                    <div className="col-span-2">
                      <label className="label">Rota de entrega</label>
                      <select className="input" value={(editing as any).rota_id || ''}
                        onChange={e => setEditing(p => ({ ...p, rota_id: e.target.value ? Number(e.target.value) : null } as any))}>
                        <option value="">Sem rota</option>
                        {rotasFrete.map((r: any) => <option key={r.id} value={r.id}>{r.nome}</option>)}
                      </select>
                    </div>
                  )}
                  {tipo === 'cliente' && (
                    <div>
                      <label className="label">Ramo de atividade</label>
                      <input className="input" list="ramos-atividade" placeholder="Ex: Sorveteria, Mercadinho..."
                        value={(editing as any).ramo_atividade || ''}
                        onChange={e => setEditing(p => ({ ...p, ramo_atividade: e.target.value } as any))} />
                      <datalist id="ramos-atividade">
                        <option value="Sorveteria" /><option value="Mercadinho" /><option value="Padaria" />
                        <option value="Lanchonete" /><option value="Conveniência" /><option value="Restaurante" />
                        <option value="Distribuidor" /><option value="Supermercado" /><option value="Bar" />
                      </datalist>
                    </div>
                  )}
                  {tipo === 'cliente' && (
                    <div>
                      <label className="label">Limite de crédito</label>
                      <input className="input" type="number" step="0.01" placeholder="0,00"
                        value={(editing as any).limite_credito ?? ''}
                        onChange={e => setEditing(p => ({ ...p, limite_credito: e.target.value ? parseFloat(e.target.value) : null } as any))} />
                      <p className="text-[10px] text-ink-400 mt-0.5">Nível de cobrança (boleto/à vista) é definido na Análise de Crédito.</p>
                    </div>
                  )}
                  {tipo === 'cliente' && editing.id && (
                    <div>
                      <label className="label">Crédito disponível (haver)</label>
                      <CreditoClienteBadge pessoaId={editing.id} />
                    </div>
                  )}
                  {tipo === 'cliente' && (
                    <div className="col-span-2">
                      <label className="label">Vendedores</label>
                      <div className="border rounded-lg p-2 max-h-36 overflow-auto grid grid-cols-2 gap-1">
                        {vendedores.length === 0 && <span className="text-xs text-ink-400 col-span-2">Nenhum usuário marcado como vendedor.</span>}
                        {vendedores.map((v: any) => {
                          const ids: number[] = (editing as any).vendedores_ids || []
                          const on = ids.includes(v.id)
                          return (
                            <label key={v.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                              <input type="checkbox" checked={on}
                                onChange={() => setEditing(p => ({ ...p, vendedores_ids: on ? ids.filter(x => x !== v.id) : [...ids, v.id] } as any))} />
                              <span className="truncate">{v.nome}</span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* SEÇÃO: Observação */}
              <div className="pt-2 border-t">
                <label className="label">Observação</label>
                <textarea className="input" rows={2} value={editing.observacao || ''}
                  onChange={e => setEditing(p => ({ ...p, observacao: e.target.value }))} />
              </div>

              {/* SEÇÃO: Liberação por filial (apenas em edição) */}
              {editing.id && (
                <div className="pt-3 border-t">
                  <h3 className="text-sm font-semibold text-ink-900 mb-2 flex items-center gap-1.5">
                    🏢 Liberação por Filial
                  </h3>
                  <FilialLiberacoes entidade="pessoa" entidadeId={editing.id} />
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t flex gap-2 sticky bottom-0 bg-white">
              <button onClick={() => setShowForm(false)} className="btn-secondary flex-1">Cancelar</button>
              <button onClick={() => save.mutate(editing)} disabled={!editing.nome || save.isPending || !!docDup || docInvalido}
                title={docDup ? `${docDup.tipo_doc} já cadastrado` : docInvalido ? `${editing.pessoa_tipo === 'PF' ? 'CPF' : 'CNPJ'} inválido` : undefined}
                className="btn-primary flex-1">
                {save.isPending ? <><Loader2 size={14} className="animate-spin inline" /> Salvando...</>
                  : (editing.id ? 'Salvar' : 'Criar')}
              </button>
            </div>
            </>
            )}
          </div>
        </div>
      )}

      {/* CLIENTE GENÉRICO DE BALCÃO (21/10) — marcação com senha de admin/gerente */}
      {generico && (
        <ModalClienteGenerico pessoa={generico}
          onClose={() => setGenerico(null)}
          onOk={() => { setGenerico(null); qc.invalidateQueries({ queryKey: ['pessoas'] }) }} />
      )}
    </div>
  )
}


// Histórico do cliente: 2 visões — Compras (pedidos) e Financeiro (títulos + crédito). Read-only.
function ClienteHistorico({ pessoaId }: { pessoaId: number }) {
  const [sub, setSub] = useState<'compras' | 'financeiro'>('compras')
  // FEATURE (03/07): clicar numa compra abre modal com o detalhe completo.
  const [compraSel, setCompraSel] = useState<number | null>(null)
  const { data, isLoading } = useQuery<any>({
    queryKey: ['cliente-historico', pessoaId],
    queryFn: async () => (await api.get(`/cadastros/pessoas/${pessoaId}/historico`)).data,
  })
  const fmt = (v: number) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  const fmtData = (s?: string | null) => s ? new Date(s).toLocaleDateString('pt-BR') : '—'

  if (isLoading) return (
    <div className="p-10 text-center text-ink-400">
      <Loader2 className="animate-spin inline" size={18} /> Carregando histórico...
    </div>
  )

  const compras = data?.compras ?? []
  const cr = data?.compras_resumo ?? {}
  const titulos = data?.titulos ?? []
  const tr = data?.titulos_resumo ?? {}
  const credito = data?.credito ?? { saldo: 0, movimentos: [] }

  const Kpi = ({ label, valor, cor }: { label: string; valor: string; cor?: string }) => (
    <div className="border border-ink-200 rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-ink-400">{label}</div>
      <div className={`text-base font-semibold ${cor || 'text-ink-800'}`}>{valor}</div>
    </div>
  )
  const badge = (s: string, atrasado?: boolean) => {
    if (atrasado) return 'bg-rose-100 text-rose-700'
    return ({
      pago: 'bg-emerald-100 text-emerald-700', aberto: 'bg-amber-100 text-amber-700',
      parcial: 'bg-blue-100 text-blue-700', cancelado: 'bg-rose-100 text-rose-700',
    } as Record<string, string>)[s] || 'bg-slate-100 text-slate-700'
  }

  return (
    <div className="p-5 space-y-4">
      <div className="flex gap-2">
        {([['compras', `Compras (${cr.qtd ?? 0})`], ['financeiro', `Financeiro (${tr.qtd ?? 0})`]] as const).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setSub(k)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${sub === k
              ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-600 hover:bg-ink-200'}`}>
            {label}
          </button>
        ))}
      </div>

      {sub === 'compras' ? (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Kpi label="Pedidos" valor={String(cr.qtd ?? 0)} />
            <Kpi label="Total comprado" valor={fmt(cr.total)} />
            <Kpi label="Última compra" valor={fmtData(cr.ultima)} />
          </div>
          <div className="border border-ink-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-xs text-ink-500 uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Data</th>
                  <th className="text-left px-3 py-2">Pedido</th>
                  <th className="text-left px-3 py-2">Forma</th>
                  <th className="text-right px-3 py-2">Total</th>
                  <th className="text-center px-3 py-2">Status</th>
                  <th className="w-6"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {compras.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-ink-400">Sem compras.</td></tr>}
                {compras.map((c: any) => (
                  <tr key={c.id} onClick={() => setCompraSel(c.id)}
                    className="hover:bg-violet-50/50 cursor-pointer" title="Ver detalhes da venda">
                    <td className="px-3 py-2 text-ink-600">{fmtData(c.data)}</td>
                    <td className="px-3 py-2 font-medium">#{c.numero}</td>
                    <td className="px-3 py-2 text-ink-500 capitalize">{(c.forma_pagamento || '').replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmt(c.total)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge(c.status)}`}>{c.status}</span>
                    </td>
                    <td className="px-1 text-ink-300"><ChevronRight size={15} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {compraSel != null && (
            <CompraDetalheModal pessoaId={pessoaId} pedidoId={compraSel} onClose={() => setCompraSel(null)} />
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <Kpi label="Em aberto" valor={fmt(tr.em_aberto)} cor="text-amber-600" />
            <Kpi label="Atrasado" valor={fmt(tr.atrasado)} cor={tr.atrasado > 0 ? 'text-rose-600' : undefined} />
            <Kpi label="Já pago" valor={fmt(tr.pago)} cor="text-emerald-600" />
            <Kpi label="Crédito (haver)" valor={fmt(credito.saldo)} cor={credito.saldo > 0 ? 'text-emerald-600' : undefined} />
          </div>

          <div className="border border-ink-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-ink-50 text-xs font-semibold text-ink-500 uppercase">Títulos a receber</div>
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-xs text-ink-500 uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Vencimento</th>
                  <th className="text-left px-3 py-2">Descrição</th>
                  <th className="text-center px-3 py-2">Parc.</th>
                  <th className="text-right px-3 py-2">Valor</th>
                  <th className="text-right px-3 py-2">Pago</th>
                  <th className="text-center px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {titulos.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-ink-400">Sem títulos.</td></tr>}
                {titulos.map((t: any) => (
                  <tr key={t.id} className="hover:bg-violet-50/50">
                    <td className="px-3 py-2 text-ink-600">{fmtData(t.vencimento)}</td>
                    <td className="px-3 py-2">{t.descricao}</td>
                    <td className="px-3 py-2 text-center text-ink-500">{t.parcela}/{t.total_parcelas}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmt(t.valor)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{fmt(t.valor_pago)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge(t.status, t.atrasado)}`}>
                        {t.atrasado ? 'atrasado' : t.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {credito.movimentos.length > 0 && (
            <div className="border border-ink-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-ink-50 text-xs font-semibold text-ink-500 uppercase">Extrato de crédito (haver)</div>
              <table className="w-full text-sm">
                <thead className="bg-ink-50 text-xs text-ink-500 uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Data</th>
                    <th className="text-left px-3 py-2">Descrição</th>
                    <th className="text-left px-3 py-2">Origem</th>
                    <th className="text-right px-3 py-2">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {credito.movimentos.map((m: any) => (
                    <tr key={m.id}>
                      <td className="px-3 py-2 text-ink-600">{fmtData(m.data)}</td>
                      <td className="px-3 py-2">{m.descricao || '—'}</td>
                      <td className="px-3 py-2 text-ink-500 capitalize">{m.origem}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${m.tipo === 'entrada' ? 'text-emerald-700' : 'text-rose-600'}`}>
                        {m.tipo === 'entrada' ? '+' : '−'}{fmt(m.valor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}


// FEATURE (03/07): modal com o detalhe COMPLETO de uma venda do histórico do cliente
// — data/hora, filial, vendedor, oficina (OS), status, itens, formas de pagamento e
// totais. Read-only. Fonte: GET /cadastros/pessoas/{pid}/compra/{pedido_id}.
function CompraDetalheModal({ pessoaId, pedidoId, onClose }: { pessoaId: number; pedidoId: number; onClose: () => void }) {
  const { data, isLoading } = useQuery<any>({
    queryKey: ['compra-detalhe', pessoaId, pedidoId],
    queryFn: async () => (await api.get(`/cadastros/pessoas/${pessoaId}/compra/${pedidoId}`)).data,
  })
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose() } }
    window.addEventListener('keydown', h, true)
    return () => window.removeEventListener('keydown', h, true)
  }, [onClose])

  const fmt = (v: number) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  const fmtDataHora = (s?: string | null) => s
    ? new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—'
  const labelForma = (f?: string | null) => (f || '').replace(/_/g, ' ')
  const badge = (s: string) => (({
    pago: 'bg-emerald-100 text-emerald-700', faturado: 'bg-emerald-100 text-emerald-700',
    aberto: 'bg-amber-100 text-amber-700', em_aberto: 'bg-amber-100 text-amber-700',
    parcial: 'bg-blue-100 text-blue-700', cancelado: 'bg-rose-100 text-rose-700',
  } as Record<string, string>)[s] || 'bg-slate-100 text-slate-700')

  const Info = ({ icon, label, valor }: { icon: ReactNode; label: string; valor?: ReactNode }) => (
    <div className="flex items-start gap-2">
      <span className="text-ink-400 mt-0.5">{icon}</span>
      <div>
        <div className="text-[10px] uppercase tracking-wide text-ink-400">{label}</div>
        <div className="text-sm text-ink-800 font-medium">{valor || '—'}</div>
      </div>
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2 font-semibold text-ink-800">
            <Receipt size={17} className="text-brand-600" />
            Venda #{data?.numero ?? '…'}
            {data?.status && <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge(data.status)}`}>{data.status}</span>}
          </div>
          <button type="button" onClick={onClose} className="text-ink-400 hover:text-ink-700"><X size={18} /></button>
        </div>

        {isLoading || !data ? (
          <div className="p-10 text-center text-ink-400"><Loader2 className="animate-spin inline" size={18} /> Carregando...</div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Info icon={<Calendar size={14} />} label="Data / hora" valor={fmtDataHora(data.criado_em)} />
              <Info icon={<Store size={14} />} label="Filial" valor={data.filial_nome} />
              <Info icon={<User size={14} />} label="Vendedor" valor={data.vendedor_nome} />
              {data.os_numero && <Info icon={<Wrench size={14} />} label="Oficina (OS)" valor={`#${String(data.os_numero).padStart(6, '0')}`} />}
              <Info icon={<Package size={14} />} label="Origem" valor={<span className="capitalize">{labelForma(data.origem)}{data.canal ? ` · ${labelForma(data.canal)}` : ''}</span>} />
              <Info icon={<CreditCard size={14} />} label="Pagamento" valor={<span className="capitalize">{labelForma(data.forma_pagamento)}{data.parcelas > 1 ? ` ${data.parcelas}x` : ''}</span>} />
            </div>

            {/* Itens */}
            <div className="border border-ink-200 rounded-lg overflow-hidden">
              <div className="px-3 py-2 bg-ink-50 text-xs font-semibold text-ink-500 uppercase flex items-center gap-1.5">
                <Package size={13} /> Produtos ({data.itens?.length ?? 0})
              </div>
              <table className="w-full text-sm">
                <thead className="bg-ink-50 text-xs text-ink-500 uppercase">
                  <tr>
                    <th className="text-left px-3 py-1.5">Cód</th>
                    <th className="text-left px-3 py-1.5">Produto</th>
                    <th className="text-right px-3 py-1.5">Qtd</th>
                    <th className="text-right px-3 py-1.5">Preço</th>
                    <th className="text-right px-3 py-1.5">Desc.</th>
                    <th className="text-right px-3 py-1.5">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {(data.itens ?? []).map((it: any, idx: number) => (
                    <tr key={idx} className="hover:bg-violet-50/50">
                      <td className="px-3 py-1.5 text-ink-500">{it.codigo || '—'}</td>
                      <td className="px-3 py-1.5">{it.descricao}</td>
                      <td className="px-3 py-1.5 text-right">{it.qtd.toLocaleString('pt-BR')}</td>
                      <td className="px-3 py-1.5 text-right">{fmt(it.preco)}</td>
                      <td className="px-3 py-1.5 text-right text-ink-500">{it.desconto ? fmt(it.desconto) : '—'}</td>
                      <td className="px-3 py-1.5 text-right font-semibold">{fmt(it.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Formas de pagamento (split) */}
            {(data.pagamentos?.length ?? 0) > 0 && (
              <div className="border border-ink-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-ink-50 text-xs font-semibold text-ink-500 uppercase flex items-center gap-1.5">
                  <CreditCard size={13} /> Formas de pagamento
                </div>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-ink-100">
                    {data.pagamentos.map((pg: any, idx: number) => (
                      <tr key={idx} className="hover:bg-violet-50/50">
                        <td className="px-3 py-1.5 capitalize">
                          {labelForma(pg.forma)}
                          {pg.bandeira ? ` · ${pg.bandeira}` : ''}{pg.parcelas > 1 ? ` ${pg.parcelas}x` : ''}
                          {pg.forma === 'recebimento_bike' && pg.bike ? ` · ${[pg.bike.marca, pg.bike.modelo].filter(Boolean).join(' ')}` : ''}
                        </td>
                        <td className={`px-3 py-1.5 text-right font-semibold ${pg.forma === 'recebimento_bike' ? 'text-rose-600' : ''}`}>
                          {pg.forma === 'recebimento_bike' ? '−' : ''}{fmt(pg.valor)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Totais */}
            <div className="flex flex-col items-end gap-1 text-sm">
              <div className="flex justify-between w-56"><span className="text-ink-500">Subtotal</span><span>{fmt(data.subtotal)}</span></div>
              {data.desconto > 0 && <div className="flex justify-between w-56"><span className="text-ink-500">Desconto</span><span className="text-rose-600">−{fmt(data.desconto)}</span></div>}
              {data.bike_troca > 0 && <div className="flex justify-between w-56"><span className="text-ink-500">Bike (troca)</span><span className="text-rose-600">−{fmt(data.bike_troca)}</span></div>}
              <div className="flex justify-between w-56 text-base font-bold border-t border-ink-200 pt-1">
                <span>Total</span><span className="text-brand-600">{fmt(data.total - (data.bike_troca || 0))}</span>
              </div>
            </div>

            {data.observacao && (
              <div className="text-sm bg-ink-50 rounded-lg px-3 py-2">
                <span className="text-[10px] uppercase tracking-wide text-ink-400 block">Observação</span>
                {data.observacao}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}


// Mostra o saldo de crédito (haver) do cliente no cadastro. Read-only.
function CreditoClienteBadge({ pessoaId }: { pessoaId: number }) {
  const { data, isLoading } = useQuery<{ saldo: number }>({
    queryKey: ['credito-cliente', pessoaId],
    queryFn: async () => (await api.get(`/financeiro/creditos/${pessoaId}`)).data,
  })
  const saldo = data?.saldo ?? 0
  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  return (
    <div className={`input flex items-center justify-between font-semibold ${
      saldo > 0 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-ink-500'
    }`}>
      <span>{isLoading ? '...' : fmt(saldo)}</span>
      {saldo > 0 && <span className="text-[10px] font-normal text-emerald-600">a favor do cliente</span>}
    </div>
  )
}

// ============ CLIENTE GENÉRICO DE BALCÃO (21/10) ============
// Marca/desmarca o cadastro tampa-buraco que dispensa a trava de cliente completo.
// É decisão de configuração, não de operação: senha de admin/gerente e motivo.
function ModalClienteGenerico({ pessoa, onClose, onOk }: {
  pessoa: Pessoa
  onClose: () => void
  onOk: () => void
}) {
  const ligando = !pessoa.cliente_generico
  const [motivo, setMotivo] = useState(pessoa.cliente_generico_motivo || '')
  const [senha, setSenha] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function confirmar() {
    if (ligando && motivo.trim().length < 5) { toast.error('Informe o motivo'); return }
    if (!senha) { toast.error('Informe a senha'); return }
    setSalvando(true)
    try {
      await api.post(`/cadastros/pessoas/${pessoa.id}/cliente-generico`,
        { generico: ligando, motivo: motivo.trim(), senha })
      toast.success(ligando ? 'Marcado como genérico' : 'Marcação removida')
      onOk()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Não foi possível salvar')
    } finally { setSalvando(false) }
  }

  return (
    <div className="fixed inset-0 bg-ink-950/80 z-[80] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="font-semibold text-ink-900">
          {ligando ? 'Marcar como cliente genérico' : 'Remover marcação de genérico'}
        </div>
        <div className="text-xs text-ink-500 mt-1">{pessoa.nome}</div>

        {ligando && (
          <>
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Esse cadastro passa a fechar venda <strong>sem CPF, endereço ou telefone</strong>.
              Use só no registro de balcão — nunca num cliente real.
            </div>
            <label className="block mt-3">
              <span className="text-[10px] uppercase tracking-wide text-ink-500">Motivo</span>
              <input value={motivo} onChange={e => setMotivo(e.target.value)} autoFocus
                placeholder="Ex.: registro de venda balcão sem identificação"
                className="w-full mt-0.5 px-3 py-2 border border-ink-300 rounded-lg text-sm text-ink-900 focus:border-brand-500 focus:outline-none" />
            </label>
          </>
        )}

        <label className="block mt-3">
          <span className="text-[10px] uppercase tracking-wide text-ink-500">Senha de admin ou gerente</span>
          <input type="password" value={senha} onChange={e => setSenha(e.target.value)}
            autoFocus={!ligando} autoComplete="off"
            onKeyDown={e => { if (e.key === 'Enter') confirmar() }}
            className="w-full mt-0.5 px-3 py-2 border border-ink-300 rounded-lg text-sm text-ink-900 focus:border-brand-500 focus:outline-none" />
        </label>

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-ink-600 hover:bg-ink-100">Cancelar</button>
          <button onClick={confirmar} disabled={salvando}
            className="px-4 py-1.5 rounded-lg text-sm font-medium bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-50">
            {salvando ? 'Salvando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  )
}
